import type {ObjectRecord} from '@/matic/features/master-data/types/master-data-types';
import type {OttoSessionContext} from '@/matic/features/agents/otto/types/otto-models';
import type {ScopeItem} from '@/matic/features/relay/lib/scope-utils';

/** The (typeId, recordId) pairs a conversation is scoped to — drawn from its
 *  anchor refs, used to restore the Library scope when the conversation opens. */
export function scopeItemsFromContext(
  context: OttoSessionContext | undefined
): ScopeItem[] {
  if (!context) return [];
  return context.anchorRefs.map((a) => ({
    typeId: a.typeId,
    recordId: a.recordId,
  }));
}

type SessionContextChip = {
  key: string;
  label: string;
  kindLabel?: string;
  taggedKind?: string;
  taggedId?: string;
};

type SessionContextDetails = {
  anchors: SessionContextChip[];
  context: SessionContextChip[];
};

function recordLabel(
  recordId: string,
  recordsByType: Record<string, ObjectRecord[]>
): string | undefined {
  for (const records of Object.values(recordsByType)) {
    const record = records.find((r) => r.id === recordId);
    if (record) return record.display ?? record.code ?? recordId.slice(0, 8);
  }
}

function taggedKindLabel(kind: string): string {
  switch (kind) {
    case 'document': {
      return 'Document';
    }

    case 'file': {
      return 'File';
    }

    case 'record': {
      return 'Record';
    }

    case 'thread': {
      return 'Thread';
    }

    default: {
      return kind;
    }
  }
}

export function resolveSessionContextDetails(
  context: OttoSessionContext | undefined,
  recordsByType: Record<string, ObjectRecord[]>
): SessionContextDetails {
  if (!context) return {anchors: [], context: []};

  const anchors = context.anchorRefs.map((anchor) => ({
    key: `anchor:${anchor.typeId}:${anchor.recordId}`,
    label:
      anchor.displayName ??
      recordLabel(anchor.recordId, recordsByType) ??
      anchor.recordId.slice(0, 8),
    kindLabel: anchor.typeName,
  }));

  const tagged = context.taggedRefs.map((tag) => {
    const kindLabel = taggedKindLabel(tag.kind);
    return {
      key: `tag:${tag.kind}:${tag.id}`,
      label:
        tag.displayLabel ??
        (tag.kind === 'record'
          ? recordLabel(tag.id, recordsByType)
          : undefined) ??
        `${kindLabel} ${tag.id.slice(0, 8)}`,
      kindLabel,
      taggedKind: tag.kind,
      taggedId: tag.id,
    };
  });

  return {anchors, context: tagged};
}

export function formatSessionDate(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  const diffDays = Math.floor(
    (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
}
